import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Calendar,
    Button,
    Card,
    Column,
    ColumnFilterElementTemplateOptions,
    DataTable,
    DataTableFilterMeta,
    DDCountries,
    DDStatus,
    Dialog,
    Dropdown,
    FilterMatchMode,
    ProgressBar,
    ReactCountryFlag,
    SCIcon,
    Badge,
    InputNumber,
    InputText,
    Tag
} from '../../Common/Imports';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { ApiResponse, Countries_EXT, Reservations_EXT } from '../../Models/Classes';
import OperativePaxes from './Components/Paxes';
import OperativeContacts from './Components/Contacts';
import OperativeComunications from './Components/Comunications';
import ReservationDetail from '../Reservations/Reservation_Detail';
import { DDHotelNames } from '../../Components/Dropdowns/DDHotelNames';
import { MailProvider } from '../Mail/Context';
import MailNew from '../Mail/New';
import { ToDateStringNoTime } from '../../Common/Utility/ToDateStringNoTime';
import { ExcelExport } from '../../Common/Utility/ExcelExport';
import { PermissionHelper, useHasPermission, usePageID, usePermissions } from '../../Helpers/Permissions/PermissionContext';

interface FilterOption {
    label: string;
    value: string;
}

const buildTextFilterOptions = (values: Array<string | undefined | null>): FilterOption[] => {
    return Array.from(new Set(values
        .map(value => value?.trim())
        .filter((value): value is string => !!value)))
        .sort((a, b) => a.localeCompare(b))
        .map(value => ({ label: value, value }));
};

const toLocalDateTimeString = (value: Date | null): string | null => {
    if (!value) {
        return null;
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
};

interface OperativeMaskFilters {
    StartDate: Date | null;
    EndDate: Date | null;
    IdReservation: number | null;
    IdReservationOperationalStatus: number | null;
    IdReservationCustomerStatus: number | null;
    StakeholderName: string;
    BrandName: string;
    ProviderName: string;
    ProviderReservationCode: string;
    CustomerReservationCode: string;
    PropertyName: string;
    CountryId: number | null;
}

const createDefaultFilters = (): OperativeMaskFilters => {
    const today = new Date();
    const startDate = new Date(today);
    const endDate = new Date(today);
    startDate.setDate(today.getDate() - 7);
    endDate.setDate(today.getDate() + 7);

    return {
        StartDate: startDate,
        EndDate: endDate,
        IdReservation: null,
        IdReservationOperationalStatus: null,
        IdReservationCustomerStatus: null,
        StakeholderName: '',
        BrandName: '',
        ProviderName: '',
        ProviderReservationCode: '',
        CustomerReservationCode: '',
        PropertyName: '',
        CountryId: null
    };
};

export const OperativeList: React.FC = () => {
    const { pageID } = usePageID();
    const P_MailToHotel = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 8 };
    const P_Contacts_Edit = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 9 };
    const P_Status_Edit = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 10 };
    const HasStatusEditPermission = useHasPermission(P_Status_Edit);
    const { t } = useTranslation();
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const [TableData, SetTableData] = useState<Reservations_EXT[]>([]);
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [IDReservation, SetIDReservation] = useState<number | undefined>();
    const [SelectedType, setSelectedType] = useState<number | undefined>();
    const [SelectedData, setSelectedData] = useState<Reservations_EXT | undefined>();
    const [type, setType] = useState<string | undefined>();
    const [CountriesData, setCountriesData] = useState<Countries_EXT[] | undefined>();
    const [MaskFilters, setMaskFilters] = useState<OperativeMaskFilters>(createDefaultFilters);
    const [AppliedFilters, setAppliedFilters] = useState<OperativeMaskFilters>(createDefaultFilters);
    const [TableSortField, SetTableSortField] = useState<string>('');
    const [TableSortOrder, SetTableSortOrder] = useState<1 | 0 | -1 | null | undefined>(null);
    const [TableFilters, SetTableFilters] = useState<DataTableFilterMeta>({
        idReservation: { value: null, matchMode: FilterMatchMode.CONTAINS },
        sendState: { value: null, matchMode: FilterMatchMode.CONTAINS },
        mailJobSendN: { value: null, matchMode: FilterMatchMode.GREATER_THAN },
        idReservationOperationalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
        idReservationCustomerStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
        providerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        customerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        propertyName: { value: null, matchMode: FilterMatchMode.EQUALS },
        country: { value: null, matchMode: FilterMatchMode.EQUALS },
        refPax: { value: null, matchMode: FilterMatchMode.EQUALS },
        checkInDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        checkOutDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        cancellationDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        unreadEmails: { value: null, matchMode: FilterMatchMode.GREATER_THAN },
        stakeholderName: { value: null, matchMode: FilterMatchMode.EQUALS },
        brandName: { value: null, matchMode: FilterMatchMode.EQUALS },
        providerName: { value: null, matchMode: FilterMatchMode.EQUALS }
    });


    const GetCountries = useCallback(async () => {
        try {
            const resp = await AxiosHelper.get<ApiResponse<Countries_EXT[]>>(`Countries/GetList`);
            if (resp.data.code === 0) {
                setCountriesData(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    }, [AxiosHelper]);

    const ServerFilters = useMemo(() => {
        const country = CountriesData?.find(item => item.idCountry === AppliedFilters.CountryId);

        return {
            ...AppliedFilters,
            StartDate: toLocalDateTimeString(AppliedFilters.StartDate),
            EndDate: toLocalDateTimeString(AppliedFilters.EndDate),
            CountryId: undefined,
            PropertyISOCode: country?.isO2 ?? country?.isO3
        };
    }, [AppliedFilters, CountriesData]);

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.post<ApiResponse<Reservations_EXT[]>>(
                `Operative/GetList`,
                ServerFilters
            );
            if (resp.data.code === 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    checkInDate: new Date(item.checkInDate),
                    checkOutDate: new Date(item.checkOutDate),
                    bookingDate: new Date(item.bookingDate),
                    cancellationDate: item.cancellationDate ? new Date(item.cancellationDate) : undefined,
                    country: CountriesData?.find(m => m.isO2 === item.propertyISOCode || m.isO3 === item.propertyISOCode)?.idCountry,
                    stakeholderName: item.stakeholder.name,
                    brandName: item.brand.name,
                    statusName: item.internalStatus.value
                }));
                SetTableData(processedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, CountriesData, ServerFilters]);

    useEffect(() => {
        GetCountries();
    }, [GetCountries]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const stakeholderOptions = useMemo(
        () => buildTextFilterOptions(TableData.map(item => item.stakeholderName)),
        [TableData]
    );
    const brandOptions = useMemo(
        () => buildTextFilterOptions(TableData.map(item => item.brandName)),
        [TableData]
    );
    const providerOptions = useMemo(
        () => buildTextFilterOptions(TableData.map(item => item.providerName)),
        [TableData]
    );
    const propertyOptions = useMemo(
        () => buildTextFilterOptions(TableData.map(item => item.propertyName)),
        [TableData]
    );
    const refPaxOptions = useMemo(
        () => buildTextFilterOptions(TableData.map(item => item.refPax)),
        [TableData]
    );

    const OptionFilterTemplate = (options: ColumnFilterElementTemplateOptions, filterOptions: FilterOption[]) => (
        <Dropdown
            value={options.value}
            options={filterOptions}
            optionLabel="label"
            optionValue="value"
            showClear
            filter
            className="w-100"
            placeholder="Select"
            onChange={(e) => options.filterCallback(e.value, options.index)}
        />
    );

    const DoubleBodyTemplate = (Name1: string, Name2: string) => (
        <>
            {Name1}
            <br />
            {Name2}
        </>
    );

    const CountryBodyTemplate = (Name1: string, Name2: string) => {
        const _Country = CountriesData?.find(m => m.isO2 === Name2 || m.isO3 === Name2);
        return (
            <>
                {Name1}
                <br />
                <ReactCountryFlag
                    countryCode={_Country?.isO2 ?? ""}
                    svg
                    title={_Country?.name} // Ensure title matches the data label
                    style={{ marginRight: "10px" }} />
                {_Country?.name || ""}
            </>)
    };

    const UpdateRowData = async (Data: Reservations_EXT) => {
        try {
            await AxiosHelper.post(`Reservation/UpdateReservation`, Data);
            showToast('success', 'Success', t("Common.Toasts.Update.Success"));
            GetData();
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.Update.Error"));
        }
    }

    const UpdateOperationalStatus = (RowData: Reservations_EXT, StatusId: number) => {
        RowData.idReservationOperationalStatus = StatusId;
        UpdateRowData(RowData);
    }

    const OperativeStatusTemplate = (RowData: Reservations_EXT) => {
        if (HasStatusEditPermission) {
            return <DDStatus IDType={2} value={RowData.idReservationOperationalStatus} onChange={e => { if (e) UpdateOperationalStatus(RowData, e) }} />;
        }
        else {
            return RowData.internalStatus.value
        }
    };

    const CountryFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
        <DDCountries value={options.value} onChange={(e) => options.filterCallback(e, options.index)} showClear filter />
    );

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
        <Calendar
            value={options.value}
            onChange={(e) => options.filterCallback(e.value, options.index)}
            className="w-100"
            dateFormat="dd/mm/yy"
            placeholder="dd/mm/yyyy"
            mask="99/99/9999"
        />
    );

    const StatusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
        <DDStatus
            IDType={2}
            value={options.value}
            showClear
            onChange={(e) => options.filterCallback(e, options.index)}
        />
    );

    const CustomerStatusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
        <DDStatus
            IDType={1}
            value={options.value}
            showClear
            onChange={(e) => options.filterCallback(e, options.index)}
        />
    );

    const HeaderTemplate = (
        <div className="flex justify-content-between align-items-center pt-2 pe-2 ps-2">
            <div className="flex align-items-center">
                <SCIcon Name="Refresh" Pointer OnClick={() => GetData()} />
            </div>
            <div className="flex align-items-center">
                <SCIcon Name="Export" Pointer OnClick={() => ExcelExport(TableData, TableFilters, "EXPORT_Reservations")} />
            </div>
        </div>
    );

    const UnreadEmailsFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
        <InputNumber
            value={options.value !== null && options.value !== undefined ? options.value : 0}
            onValueChange={(e) => options.filterCallback(e.value, options.index)}
            mode="decimal"
            placeholder="Unread Emails"
            className="w-100"
        />
    );

    const rowClass = (data: Reservations_EXT) => {
        return data.internalStatus.cssClasses;
    };

    const CustomerStatusTemplate = (RowData: Reservations_EXT) => {
        return <Tag className={RowData.customerStatus.cssClasses}>{RowData.customerStatus.value}</Tag>;
    };

    const IconTemplate = (data: Reservations_EXT, type: number) => {
        switch (type) {
            case 0:
                return (
                    <PermissionHelper requiredPermission={P_MailToHotel}>
                        <SCIcon
                            Name='Mail'
                            OnClick={() => {
                                setSelectedData(data);
                                setSelectedType(0);
                            }}
                            Pointer
                            Tooltip="Mail to Hotel"
                        />
                    </PermissionHelper>
                );
            case 1:
                return (
                    <SCIcon
                        Classes="text-danger"
                        Name='MailForProvider'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(1);
                        }} Pointer
                        Tooltip="Mail to Provider"
                    />);
            case 5:
                return (
                    <SCIcon
                        Name='MailForInvoice'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(5);
                        }} Pointer
                        Tooltip="Invoice Email"
                    />);
            case 2:
                return (
                    <SCIcon
                        Name='Comunications'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(2)
                        }} Pointer />);
            case 3:
                return (
                    <PermissionHelper requiredPermission={P_Contacts_Edit}>
                        <SCIcon
                            Name='Contacts'
                            OnClick={() => {
                                setSelectedData(data);
                                setSelectedType(3)
                            }} Pointer />
                    </PermissionHelper>
                )
        }
    }

    return (
        <>
            {

                SelectedData && (SelectedType === 0 || SelectedType === 1 || SelectedType === 5) &&
                <Dialog visible={true} onHide={() => setSelectedData(undefined)}>
                    <MailProvider>
                        {/* Action 5 opens the Invoice Email template (template Type 2) */}
                        <MailNew IdReservation={SelectedData.idReservation} Type={SelectedType === 5 ? 2 : SelectedType} PopupMode={true} />
                    </MailProvider>
                </Dialog>
            }
            {
                (SelectedData && SelectedType === 2) && <OperativeComunications
                    IDReservation={SelectedData.idReservation} OnHide={() => {
                        setSelectedData(undefined);
                    }} />
            }

            {
                (SelectedData && SelectedType === 3) && <OperativeContacts
                    IDReservation={SelectedData.idReservation}
                    IDProperty={SelectedData.idProperty}
                    ShowPropertyContacts
                    ShowReservationContacts
                    OnHide={() => {
                        setSelectedData(undefined);
                    }} />
            }
            {
                IDReservation &&
                <ReservationDetail
                    IDReservation={IDReservation}
                    OnHide={() => {
                        SetIDReservation(undefined);
                    }}
                />
            }
            {
                (SelectedData && SelectedType === 4) &&
                <OperativePaxes
                    IDReservation={SelectedData.idReservation} />
            }
            {
                SelectedData && type === "Contacts" &&
                <OperativeContacts
                    IDReservation={SelectedData.idReservation}
                    IDProperty={SelectedData.idProperty}
                    ShowPropertyContacts
                    ShowReservationContacts
                    OnHide={() => {
                        setSelectedData(undefined);
                        setType(undefined);
                    }}
                />
            }
            {
                SelectedData && type === "Comunications" &&
                <OperativeComunications
                    IDReservation={SelectedData.idReservation}
                    OnHide={() => {
                        setSelectedData(undefined);
                        setType(undefined);
                    }} />
            }
            {
                SelectedData && type === "Paxes" &&
                <OperativePaxes
                    IDReservation={SelectedData.idReservation} />
            }
            <Card className="mb-2 operative-filter-card">
                <div className="operative-filter-header">
                    <div>
                        <h2>Operative filters</h2>
                        <p>Narrow down the reservations loaded from the server.</p>
                    </div>
                </div>
                <div className="row g-3 operative-filter-grid">
                    <div className="col-md-2">
                        <label className="block mb-1">Check-in from</label>
                        <Calendar
                            value={MaskFilters.StartDate}
                            onChange={(e) => setMaskFilters(current => ({ ...current, StartDate: e.value as Date | null }))}
                            className="w-100"
                            showButtonBar
                            dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999" />
                    </div>
                    <div className="col-md-2">
                        <label className="block mb-1">Check-in to</label>
                        <Calendar
                            value={MaskFilters.EndDate}
                            onChange={(e) => setMaskFilters(current => ({ ...current, EndDate: e.value as Date | null }))}
                            className="w-100"
                            showButtonBar
                            dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999" />
                    </div>
                    <div className="col-md-2">
                        <label className="block mb-1">{t("Operative.Table.ResId")}</label>
                        <InputNumber
                            value={MaskFilters.IdReservation}
                            onValueChange={(e) => setMaskFilters(current => ({ ...current, IdReservation: e.value ?? null }))}
                            useGrouping={false}
                            className="w-100"
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.OperativeStatus")}</label>
                        <DDStatus
                            IDType={2}
                            value={MaskFilters.IdReservationOperationalStatus ?? undefined}
                            showClear
                            onChange={(value) => setMaskFilters(current => ({ ...current, IdReservationOperationalStatus: value ?? null }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.ResCustomerStatus")}</label>
                        <DDStatus
                            IDType={1}
                            value={MaskFilters.IdReservationCustomerStatus ?? undefined}
                            showClear
                            onChange={(value) => setMaskFilters(current => ({ ...current, IdReservationCustomerStatus: value ?? null }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.ProviderReservationCode")}</label>
                        <InputText
                            value={MaskFilters.ProviderReservationCode}
                            onChange={(e) => setMaskFilters(current => ({ ...current, ProviderReservationCode: e.target.value }))}
                            className="w-100"
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.CustomerReservationCode")}</label>
                        <InputText
                            value={MaskFilters.CustomerReservationCode}
                            onChange={(e) => setMaskFilters(current => ({ ...current, CustomerReservationCode: e.target.value }))}
                            className="w-100"
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.HotelName")}</label>
                        <DDHotelNames
                            value={MaskFilters.PropertyName}
                            onChange={(value) => setMaskFilters(current => ({ ...current, PropertyName: value }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.Country")}</label>
                        <DDCountries
                            value={MaskFilters.CountryId ?? undefined}
                            onChange={(value) => setMaskFilters(current => ({ ...current, CountryId: value ?? null }))}
                            showClear
                            filter
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.Stakeholder")}</label>
                        <InputText
                            value={MaskFilters.StakeholderName}
                            onChange={(e) => setMaskFilters(current => ({ ...current, StakeholderName: e.target.value }))}
                            className="w-100"
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.Brand")}</label>
                        <InputText
                            value={MaskFilters.BrandName}
                            onChange={(e) => setMaskFilters(current => ({ ...current, BrandName: e.target.value }))}
                            className="w-100"
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="block mb-1">{t("Operative.Table.ProviderName")}</label>
                        <InputText
                            value={MaskFilters.ProviderName}
                            onChange={(e) => setMaskFilters(current => ({ ...current, ProviderName: e.target.value }))}
                            className="w-100"
                        />
                    </div>
                    <div className="col-md-3 flex align-items-end gap-2 operative-filter-actions">
                        <Button
                            label="Search"
                            icon="pi pi-search"
                            onClick={() => setAppliedFilters({ ...MaskFilters })}
                        />
                        <Button
                            label="Reset"
                            icon="pi pi-filter-slash"
                            severity="secondary"
                            onClick={() => {
                                const defaults = createDefaultFilters();
                                setMaskFilters(defaults);
                                setAppliedFilters({ ...defaults });
                            }}
                        />
                    </div>
                </div>
            </Card>
            <Card header={HeaderTemplate}>
                <div className="row">
                    <div className="col">
                        {TableLoader && <ProgressBar mode="indeterminate" style={{ height: '6px' }} />}
                        <DataTable
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                value={TableData}
                                size="small"
                                stripedRows
                                filterDisplay="menu"
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 20, 50]}
                                onFilter={(e) => SetTableFilters(e.filters)}
                                filters={TableFilters}
                                sortField={TableSortField}
                                sortOrder={TableSortOrder}
                                onSort={(e) => {
                                    SetTableSortField(e.sortField);
                                    SetTableSortOrder(e.sortOrder);
                                }}
                                rowClassName={rowClass}
                                tableStyle={{ minWidth: '50rem' }}>
                                <Column
                                    filter
                                    sortable
                                    field="idReservation"
                                    dataType="numeric"
                                    header={t("Operative.Table.ResId")}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    body={(e: Reservations_EXT) => (
                                        <SCIcon
                                            Pointer
                                            Name="Info"
                                            Tooltip={t("Operative.Table.OpenDetail")}
                                            OnClick={() => SetIDReservation(e.idReservation)}
                                        />
                                    )}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    body={(e: Reservations_EXT) => IconTemplate(e, 0)}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    body={(e: Reservations_EXT) => IconTemplate(e, 1)}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    body={(e: Reservations_EXT) => IconTemplate(e, 5)}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    body={(e: Reservations_EXT) => (
                                        <SCIcon
                                            Pointer
                                            Name="Contacts"
                                            OnClick={() => {
                                                setSelectedData(e);
                                                setType("Contacts");
                                            }}
                                        />
                                    )}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    field="unreadEmails"
                                    filterField="unreadEmails"
                                    filterElement={UnreadEmailsFilterTemplate}
                                    dataType="numeric"
                                    body={(e: Reservations_EXT) => (
                                        <>
                                            <SCIcon
                                                Pointer
                                                Name="Forum"
                                                OnClick={() => {
                                                    setSelectedData(e);
                                                    setType("Comunications");
                                                }}
                                            />
                                            {e.unreadEmails > 0 && <Badge value={e.unreadEmails} severity="danger" />}
                                        </>
                                    )}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    header={t("Operative.Table.SentEmails")}
                                    field="mailJobSendN"
                                    dataType="numeric"
                                    filterField="mailJobSendN"
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    header={t("Operative.Table.OperativeStatus")}
                                    field="idReservationOperationalStatus"
                                    filterElement={StatusFilterTemplate}
                                    body={(e: Reservations_EXT) => OperativeStatusTemplate(e)}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    field="customerStatus.value"
                                    header={t("Operative.Table.ResCustomerStatus")}
                                    filterField="idReservationCustomerStatus"
                                    filterElement={CustomerStatusFilterTemplate}
                                    bodyClassName="table-cell-nowrap"
                                    body={(e: Reservations_EXT) => CustomerStatusTemplate(e)}
                                />
                                <Column
                                    filter
                                    sortable
                                    field="stakeholderName"
                                    header={t("Operative.Table.Stakeholder")}
                                    filterField="stakeholderName"
                                    filterElement={(options) => OptionFilterTemplate(options, stakeholderOptions)}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    field="brandName"
                                    header={t("Operative.Table.Brand")}
                                    filterField="brandName"
                                    filterElement={(options) => OptionFilterTemplate(options, brandOptions)}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    field="providerName"
                                    header={t("Operative.Table.ProviderName")}
                                    filterField="providerName"
                                    filterElement={(options) => OptionFilterTemplate(options, providerOptions)}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    field="providerReservationCode"
                                    header={t("Operative.Table.ProviderReservationCode")}
                                    bodyClassName="table-cell-nowrap"
                                    body={(e: Reservations_EXT) => { return <a className="underline text-primary" style={{ cursor: "pointer" }} onClick={() => SetIDReservation(e.idReservation)}>{e.providerReservationCode}</a> }}
                                />
                                <Column
                                    filter
                                    sortable
                                    field="customerReservationCode"
                                    header={t("Operative.Table.CustomerReservationCode")}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    field="propertyName"
                                    header={t("Operative.Table.HotelName")}
                                    filterElement={(options) => OptionFilterTemplate(options, propertyOptions)}
                                    body={(e: Reservations_EXT) => DoubleBodyTemplate(e.propertyName, e.property?.name)}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    sortable
                                    filter
                                    field="country"
                                    filterField="country"
                                    header={t("Operative.Table.Country")}
                                    filterElement={CountryFilterTemplate}
                                    body={(e: Reservations_EXT) => CountryBodyTemplate(e.propertyISOCode, e.property?.country)}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    sortable
                                    filter
                                    field="refPax"
                                    sortField="refPax"
                                    header={t("Operative.Table.RefPax")}
                                    filterElement={(options) => OptionFilterTemplate(options, refPaxOptions)}
                                    body={(e: Reservations_EXT) => {
                                        return <a className="underline text-primary" style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                setSelectedData(e);
                                                setType("Paxes");
                                            }}>{e.refPax}</a>
                                    }}
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    dataType="date"
                                    header={t("Operative.Table.CheckIn")}
                                    body={(e: Reservations_EXT) => ToDateStringNoTime(e.checkInDate)}
                                    filterElement={dateFilterTemplate}
                                    filterField="checkInDate"
                                    sortField="checkInDate"
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    dataType="date"
                                    header={t("Operative.Table.CheckOut")}
                                    body={(e: Reservations_EXT) => ToDateStringNoTime(e.checkOutDate)}
                                    filterElement={dateFilterTemplate}
                                    filterField="checkOutDate"
                                    sortField="checkOutDate"
                                    bodyClassName="table-cell-nowrap"
                                />
                                <Column
                                    filter
                                    sortable
                                    dataType="date"
                                    header={t("Operative.Table.CancellationDate")}
                                    body={(e: Reservations_EXT) => e.cancellationDate ? ToDateStringNoTime(e.cancellationDate) : ""}
                                    filterElement={dateFilterTemplate}
                                    filterField="cancellationDate"
                                    sortField="cancellationDate"
                                    bodyClassName="table-cell-nowrap"
                                />
                        </DataTable>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default OperativeList;
